import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Loader2, UploadCloud, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { useMediaUpload } from '../lib';

import { useMediaFolder } from './media-folder';
import { resolveMediaBatch } from './media.api';

interface MediaGalleryFieldProps {
  /** Ordered list of media ids stored on the section. */
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  folder?: string;
  disabled?: boolean;
  /** Cap how many images can be stored (upload zone hides when full). */
  maxItems?: number;
}

/** One reorderable thumbnail. Drag anywhere on the tile; the X removes it. */
const SortableThumb = ({
  id,
  url,
  onRemove,
  disabled,
}: {
  id: string;
  url: string | undefined;
  onRemove: () => void;
  disabled?: boolean;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className="group relative aspect-square overflow-hidden rounded-lg border border-(--border) bg-(--z-50)"
    >
      {url ? (
        <img
          src={url}
          alt=""
          className={`h-full w-full object-cover ${disabled ? '' : 'cursor-grab active:cursor-grabbing'}`}
          {...attributes}
          {...listeners}
        />
      ) : (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-(--fg-faint)" />
        </div>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 rounded bg-black/60 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Remove image"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

/**
 * Bulk image field for galleries: select (or drop) many images at once - each
 * is uploaded via the 3-step presigned S3 flow and appended to the ordered
 * list - then drag the resulting thumbnails to reorder, or remove individual
 * ones. Stores an ordered array of media ids on the form.
 */
export const MediaGalleryField = ({
  value,
  onChange,
  label,
  folder,
  disabled,
  maxItems,
}: MediaGalleryFieldProps): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const contextFolder = useMediaFolder();
  const uploadFolder = folder ?? contextFolder;
  const { upload } = useMediaUpload<{ id: string }>();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Resolve every id → preview URL whenever the ordered list changes.
  const key = value.join(',');
  useEffect(() => {
    let cancelled = false;
    if (value.length === 0) {
      setUrls({});
      return;
    }
    resolveMediaBatch(value)
      .then((list) => {
        if (!cancelled) setUrls(Object.fromEntries(list.map((m) => [m.id, m.url])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // `key` is the order-sensitive fingerprint of `value`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const handleFiles = async (files: FileList | File[]): Promise<void> => {
    if (disabled) return;
    const slotsLeft = maxItems === undefined ? Infinity : Math.max(0, maxItems - value.length);
    if (slotsLeft === 0) return;
    const images = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, slotsLeft);
    if (images.length === 0) return;
    setRemaining(images.length);
    const added: string[] = [];
    for (const file of images) {
      try {
        const asset = await upload(file, { kind: 'IMAGE', folder: uploadFolder });
        if (asset?.id) added.push(asset.id);
      } catch {
        /* skip failed file, keep going */
      }
      setRemaining((r) => Math.max(0, r - 1));
    }
    if (added.length) onChange([...value, ...added].slice(0, maxItems));
  };

  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = value.indexOf(String(active.id));
    const to = value.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onChange(arrayMove(value, from, to));
  };

  const uploading = remaining > 0;
  const atMax = maxItems !== undefined && value.length >= maxItems;

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-(--fg-muted)">{label}</label>
      )}

      {value.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={value} strategy={rectSortingStrategy}>
            <div className="mb-2 grid grid-cols-4 gap-2 sm:grid-cols-5">
              {value.map((id) => (
                <SortableThumb
                  key={id}
                  id={id}
                  url={urls[id]}
                  disabled={disabled}
                  onRemove={() => onChange(value.filter((x) => x !== id))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {!atMax && (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
          }}
          className={`flex min-h-[88px] w-full flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
            dragOver
              ? 'border-(--primary) bg-(--accent)'
              : 'border-(--border-strong) bg-(--z-50) hover:border-(--primary)'
          } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin text-(--primary)" />
              <span className="text-xs text-(--fg-muted)">Uploading… {remaining} left</span>
            </>
          ) : (
            <>
              <UploadCloud className="h-6 w-6 text-(--fg-faint)" />
              <span className="text-sm text-(--fg-muted)">
                Drag &amp; drop or <span className="font-medium text-(--primary)">browse</span>
                {maxItems === undefined ? ' - select multiple' : ` - up to ${maxItems} images`}
              </span>
              <span className="text-[12px] text-(--fg-faint)">
                {value.length > 0
                  ? `${value.length}${maxItems !== undefined ? ` / ${maxItems}` : ''} image${value.length === 1 ? '' : 's'} · drag to reorder`
                  : maxItems !== undefined
                    ? `Add up to ${maxItems} images at once`
                    : 'images'}
              </span>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
};
